import React, { useState } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import overlayFactory from 'react-bootstrap-table2-overlay';
import paginationFactory from 'react-bootstrap-table2-paginator';
import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { usePersonalHistoryOpen } from '../../containers/MedicalRecords/Hooks/usePersonalHistoryOpen';
import { NullableDatePickerCombo } from '../CustomControls/DatePicker';
import { ExpandableContent } from '../CustomControls/ExpandableContent';
import { View } from '../Icons/Icons';
import { Tooltip } from '../Utils/Tooltips';

export default function PersonalHistoryOpenItems({ value, name, onChange, onBlur, medicalRecordNumber, patientNumber, expandedAll, isDisabled, title, protocolId, visitId }) {
    const [adverseEvents, query, totalSize, isLoading, handleTableChange] = usePersonalHistoryOpen(medicalRecordNumber, null, true, protocolId, visitId);
    const [closedItems, setClosedItems] = useState(value !== "" && value !== null ? value : []);
    const { t } = useTranslation();

    const handleChange = (item, fieldName, value) => {
        let dateName = fieldName.split(".")[1];
        var closedItem = closedItems.find(x => x.id === item.id);

        if (closedItem) { // Update
            closedItem.closed[dateName] = value;

            if (!closedItem.closed.toDay && !closedItem.closed.toMonth && !closedItem.closed.toYear) {
                const closedItemsCopy = closedItems.filter(x => x.id !== closedItem.id); // Remove
                setClosedItems(closedItemsCopy);
                onChange(name, closedItemsCopy);
            } else {
                const closedItemsCopy = JSON.parse(JSON.stringify(closedItems));
                var index = closedItemsCopy.findIndex(x => x.id === item.id);
                closedItemsCopy.splice(index, 1, closedItem); // Update. 
                setClosedItems(closedItemsCopy);
                onChange(name, closedItemsCopy);
            }
        }
        else {
            // New
            var joined = closedItems.concat({
                id: item.id,
                closed: { [dateName]: value },
                name: item.adverseEffect
            });

            setClosedItems(joined);
            onChange(name, joined);
        }
    }

    const handleBlur = () => {
        if (onBlur)
            onBlur(name, true);
    };

    const columns = [
        { dataField: 'adverseEffect', text: t('medicalRecords.personalHistory.adverseEffect') },
        { dataField: 'from', text: t('medicalRecords.from') },
        {
            isDummyField: true,
            dataField: 'to',
            text: t('medicalRecords.to'),
            formatter: function (_cell, row, rowindex) {
                var closedItem = closedItems?.find(x => x.id === row.id);
                const isClosed = row.to !== "" && row.to !== null && row.toYear !== "" && row.toYear !== null;

                if (isClosed) return "";
                return (<span className="item-text">
                    <NullableDatePickerCombo
                        nameDay={`${rowindex}.toDay`}
                        nameMonth={`${rowindex}.toMonth`}
                        nameYear={`${rowindex}.toYear`}
                        onChange={(name, value) => handleChange(row, name, value)}
                        valueDay={closedItem ? closedItem.closed.toDay : row.toDay}
                        valueMonth={closedItem ? closedItem.closed.toMonth : row.toMonth}
                        valueYear={closedItem ? closedItem.closed.toYear : row.toYear}
                        onBlur={handleBlur}
                        disabled={isDisabled}
                    />
                </span>)
            }
        },
        {
            dataField: 'actions',
            text: t("commons.actions"),
            isDummyField: true,
            headerClasses: 'header-actions',
            classes: 'row-actions',
            formatter: function (_cell, row, rowIndex) {
                if (!protocolId || !patientNumber || !medicalRecordNumber)
                    return;
                return (
                    <div key={`${Math.random()}-actions-${rowIndex}`}>
                        {
                            row.visitId ?
                                <Tooltip tooltip={t("medicalRecords.viewVisit")} id={"tooltipview-" + row.id}>
                                    <NavLink to={`/admin/protocols/${protocolId}/visitTracking/${row.visitId}/patient/${patientNumber}`} className="nav-link" activeClassName="active">
                                        <View />
                                    </NavLink>
                                </Tooltip>
                                :
                                <Tooltip tooltip={t("medicalRecords.viewVisit")} id={"tooltipview-" + row.id}>
                                    <NavLink to={`/admin/medicalRecords/${medicalRecordNumber}/entry/${row.entryId}`} className="nav-link" activeClassName="active">
                                        <View />
                                    </NavLink>
                                </Tooltip>
                        }
                    </div>
                )
            }
        }
    ];

    const columnsClosed = [
        { dataField: 'name', text: t('medicalRecords.personalHistory.adverseEffect') },
        {
            isDummyField: true,
            dataField: 'to',
            text: t('medicalRecords.to'),
            formatter: function (_cell, row, rowindex) {
                if (!row.closed) return "";

                return (
                    <span className="item-text">
                        <NullableDatePickerCombo
                            nameDay={`${rowindex}.toDay`}
                            nameMonth={`${rowindex}.toMonth`}
                            nameYear={`${rowindex}.toYear`}
                            onChange={(inputName, value) => handleChange(row, inputName, value)}
                            onBlur={handleBlur}
                            valueDay={row.closed.toDay}
                            valueMonth={row.closed.toMonth}
                            valueYear={row.closed.toYear}
                            disabled={isDisabled}
                        />
                    </span>
                )
            }
        }
    ];

    return (
        <>
            {
                value && value.length > 0 &&
                <ExpandableContent title={t("medicalRecords.adverseEvents.closed")} expandedAll={expandedAll} content="adverseEvents">
                    <BootstrapTable
                        bordered={false}
                        classes="table-spaced table-spaced-small"
                        wrapperClasses="table-responsive"
                        striped
                        hover
                        keyField='id'
                        data={value ? value : []}
                        columns={columnsClosed}
                    />
                </ExpandableContent>
            }
            <ExpandableContent title={title} expandedAll={expandedAll} content="adverseEvents">
                <BootstrapTable
                    bordered={false}
                    classes="table-spaced table-spaced-small"
                    wrapperClasses="table-responsive"
                    striped
                    hover
                    remote
                    keyField='id'
                    data={adverseEvents ? adverseEvents : []}
                    columns={columns}
                    pagination={query && query.pagination && paginationFactory({
                        page: query.pagination.page,
                        sizePerPage: query.pagination.sizePerPage,
                        totalSize: totalSize,
                        hideSizePerPage: true,
                        hidePageListOnlyOnePage: true
                    })}
                    onTableChange={handleTableChange}
                    noDataIndication={() => <>{t("commons.noData")}</>}
                    loading={isLoading}
                    overlay={overlayFactory({ spinner: true })}
                />
            </ExpandableContent>
        </>
    );
}